import { CHECK_IN_LABELS, EVENT_STATUS_LABELS } from "@/components/service/serviceShared";
import type { ServiceCheckInMilestone, ServiceTechManifestEvent } from "@/lib/service/types";
import { buildServiceTechMapsUrl, buildTelUrl } from "@/lib/service/serviceTechMapLinks";

const TECH_MILESTONES: ServiceCheckInMilestone[] = ["en_route", "on_site", "complete"];

type Props = {
  event: ServiceTechManifestEvent;
  stamping: boolean;
  onCheckIn: (milestone: ServiceCheckInMilestone) => void;
};

export function ServiceTechEventCard({ event, stamping, onCheckIn }: Props) {
  const mapsUrl = buildServiceTechMapsUrl(event.location_address || event.location_label);
  const telUrl = buildTelUrl(event.contact_phone);

  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-start justify-between gap-2">
        <div>
          {event.sequence != null ? (
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Stop {event.sequence + 1}
            </p>
          ) : null}
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {event.title || event.location_label || "Service job"}
          </h3>
          {event.contact_name ? (
            <p className="text-sm text-slate-600 dark:text-slate-300">{event.contact_name}</p>
          ) : null}
        </div>
      </div>

      <div className="mt-1 text-xs text-slate-600 dark:text-slate-400">
        {event.scheduled_date ?? "Unscheduled"}
        {event.scheduled_window_label ? ` · ${event.scheduled_window_label}` : ""} ·{" "}
        {EVENT_STATUS_LABELS[event.status] ?? event.status}
      </div>
      <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
        {event.location_address || event.location_label || "No address on file"}
      </p>
      <div className="mt-2 text-xs text-indigo-700 dark:text-indigo-300">
        Check-in: {CHECK_IN_LABELS[event.check_in_status] ?? event.check_in_status}
      </div>

      <div className="mt-3 flex flex-wrap gap-2 text-sm">
        {mapsUrl ? (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-800 dark:bg-slate-800 dark:text-slate-100"
          >
            Maps
          </a>
        ) : null}
        {telUrl ? (
          <a
            href={telUrl}
            className="rounded-lg bg-emerald-100 px-3 py-1.5 text-xs font-medium text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100"
          >
            Call
          </a>
        ) : null}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {TECH_MILESTONES.map((milestone) => (
          <button
            key={milestone}
            type="button"
            disabled={stamping}
            onClick={() => onCheckIn(milestone)}
            className="rounded-lg border border-indigo-300 px-2 py-1 text-xs font-semibold text-indigo-800 disabled:opacity-50 dark:border-indigo-700 dark:text-indigo-200"
          >
            {CHECK_IN_LABELS[milestone]}
          </button>
        ))}
      </div>
    </article>
  );
}
